/**
 * Desktop shop grid balancer — lets the filter and cart columns "lend" a little width so the
 * product grid always lands on a whole number of full-width card columns, instead of leaving
 * the quantization leftover as outer gutters next to the cards.
 *
 * Pure CSS cannot express this: the product column's ideal width is a step function of the
 * viewport (N × card + gaps), and `minmax()`/`fr` distribute space continuously. So a
 * ResizeObserver measures the grid and solves the one-dimensional fit, then hands the result
 * back to CSS as custom properties on the `.shop-grid` element:
 *
 *   --shop-filters-width / --shop-cart-width  — panel columns, inside small clamp bands
 *   --shop-card-width                         — the solved card width, inside the card band
 *   --shop-card-stretch / --shop-grid-justify — flip the card tracks from the no-JS fallback
 *                                               (centred, capped) to fill (1fr, stretch), except
 *                                               where the capped cards leave a residue: then the
 *                                               tracks stay fixed and the row centres
 *
 * SSR/no-JS keeps the fallback layout via the `var(…, fallback)` defaults — this module only
 * ever runs client-side after mount, and only touches inline style (never the DOM tree), so
 * hydration parity is preserved.
 */

/** Panel width bands (px): how much each column may lend. Defaults mirror the CSS tokens
 * (`--panel-filters-width`, `--cart-width` in shop grid CSS); the filter panel only shrinks —
 * its content caps at the default width, so growing the column would just show a void. */
const FILTERS = {min: 280, def: 320, max: 320}
const CART = {min: 460, def: 500, max: 540}

const px = (value: string): number | null => {
    const n = parseFloat(value)
    return Number.isFinite(n) && n > 0 ? n : null
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

function balance(el: HTMLElement) {
    const cs = getComputedStyle(el)

    // Tablet/mobile collapse the grid to a single "products" track; nothing to balance there.
    const track_count = cs.gridTemplateColumns.split(' ').length
    const has_filters = !el.classList.contains('shop-grid--no-filters')
    if (track_count < 2) {
        for (const prop of [
            '--shop-filters-width',
            '--shop-cart-width',
            '--shop-card-width',
            '--shop-card-stretch',
            '--shop-grid-justify',
        ]) {
            el.style.removeProperty(prop)
        }
        return
    }

    const card_min = px(cs.getPropertyValue('--shop-card-min')) ?? 248
    const card_max = px(cs.getPropertyValue('--shop-card-max')) ?? 290
    const column_gap = px(cs.columnGap) ?? 24
    const items = el.querySelector('.c-collection-items')
    const card_gap = (items && px(getComputedStyle(items).columnGap)) || column_gap

    const filters = has_filters ? FILTERS : {min: 0, def: 0, max: 0}
    const padding = (px(cs.paddingLeft) ?? 0) + (px(cs.paddingRight) ?? 0)

    // The products column is `overflow-y: scroll`, so a classic scrollbar permanently reserves a
    // gutter *inside* that track. Solving without it makes the written card width a hair wider than
    // the track can hold, `auto-fill` drops a column, and the survivors stretch through their 1fr
    // ceiling — one giant card where two were solved for. Zero on overlay-scrollbar platforms.
    const scroller = el.querySelector<HTMLElement>(':scope > .c-collection-view, :scope > .view')
    const scrollbar = scroller ? Math.max(0, scroller.offsetWidth - scroller.clientWidth) : 0

    const total = el.clientWidth - padding - column_gap * (track_count - 1) - scrollbar

    // Most columns that fit with both panels ceding their maximum.
    const n = Math.max(1, Math.floor((total - filters.min - CART.min + card_gap) / (card_min + card_gap)))

    // Card width that would keep both panels at their defaults, held to the card band…
    const w_ideal = clamp((total - filters.def - CART.def - (n - 1) * card_gap) / n, card_min, card_max)

    // …then the panels absorb what the cards couldn't (cart first — the roomier band).
    const leftover = total - (n * w_ideal + (n - 1) * card_gap)
    const f = has_filters ? clamp(leftover - CART.def, filters.min, filters.max) : 0
    const c = clamp(leftover - f, CART.min, CART.max)

    // Final card width from the exact remaining middle, held to the card band at BOTH ends. Without
    // the card_max ceiling the top of every column band (where the panels sit pinned at their maxima
    // and `n` hasn't ticked over yet) dumped all remaining width on the cards — ~380px cards at the
    // 1-column/2-column boundary.
    const middle = total - f - c
    const w = clamp(Math.floor(((middle - (n - 1) * card_gap) / n) * 2) / 2, card_min, card_max)

    // Whatever the capped cards left over becomes even outer gutters (fixed tracks, row centred)
    // rather than over-wide cards. `n` is already the most columns the panels' minima allow, so the
    // residue can never be another full column. Under a pixel per card it's just the half-pixel
    // floor above: keep the 1fr ceiling so the tracks absorb it and the row still fills the column
    // edge to edge rather than paying a visible gutter for sub-pixel rounding.
    const residue = middle - (n * w + (n - 1) * card_gap)
    const stretch = residue > n ? `${w}px` : '1fr'

    // `auto-fill` counts columns from the FLOOR we write, and the solve above lands on an exact fit —
    // so any sub-pixel disagreement between this arithmetic and the real track (fractional device
    // pixel ratios round layout to physical pixels, and the panel widths transition into place) drops
    // a column, and the 1fr ceiling inflates the survivors to fill the hole: 3 × 397px cards where 4 ×
    // 282px were solved. Write the floor a pixel short so the count always has slack; the track max
    // (1fr, or the fixed `w`) still lands the cards on the solved width. Skipped in the rare case
    // where that pixel would let an extra column in.
    const columns_at = (min: number) => Math.max(1, Math.floor((middle + card_gap) / (min + card_gap)))
    const floor_w = columns_at(w - 1) > n ? w : w - 1

    // Skip no-op writes: each setProperty invalidates style, and the balancer may be asked to
    // re-solve for reasons that don't change the outcome.
    const set = (prop: string, value: string) => {
        if (el.style.getPropertyValue(prop) !== value) {
            el.style.setProperty(prop, value)
        }
    }

    if (has_filters) {
        set('--shop-filters-width', `${f}px`)
    }
    set('--shop-cart-width', `${c}px`)
    set('--shop-card-width', `${floor_w}px`)
    set('--shop-card-stretch', stretch)
    set('--shop-grid-justify', stretch === '1fr' ? 'stretch' : 'center')
}

/** Attach the balancer to a `.shop-grid` element; returns the detach function. */
export function observe_shop_grid(el: HTMLElement): () => void {
    if (globalThis.__SSR_MODE__ || typeof ResizeObserver === 'undefined') {
        return () => {}
    }

    // Trailing debounce: during a live window drag the stale vars keep the layout a plain fluid
    // grid (fixed panels, cards stretching through their 1fr ceiling) — re-quantizing every frame
    // made panels and card tracks churn visibly. One solve after the resize settles, and the
    // grid's `grid-template-columns` transition glides the panels to the new answer.
    let timer = 0
    let frame = 0
    // The observer also fires for height-only changes (panel content, the mobile bottom bar, the URL bar
    // collapsing) and for the grid's own `grid-template-columns` transition settling. None of those move the
    // answer, and re-solving writes custom properties that invalidate style for the whole card grid.
    let solved_width = -1
    const solve = () => {
        if (el.clientWidth === solved_width) {
            return
        }
        solved_width = el.clientWidth
        balance(el)
    }
    const schedule = () => {
        clearTimeout(timer)
        timer = window.setTimeout(() => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(solve)
        }, 150)
    }

    const observer = new ResizeObserver(schedule)
    observer.observe(el)

    // First solve immediately — the element just mounted at its final size; waiting out the
    // debounce would flash the unbalanced fallback layout.
    frame = requestAnimationFrame(solve)

    return () => {
        clearTimeout(timer)
        cancelAnimationFrame(frame)
        observer.disconnect()
    }
}
